'use client'

/**
 * The panel behind a feedback pin (Issue #13578).
 *
 * Renders the thread for one anchored feedback item, plus — for an admin —
 * the Accept / Reject / Annotate triage controls the facilitator needs
 * WITHOUT leaving the page the feedback is about.
 *
 * ## Deliberately separate from the marker
 *
 * `feedback-pin.tsx` owns the trigger, the Radix popover shell and the
 * positioning. This file owns the contents. The split is what lets every
 * property below be asserted by rendering this component directly, rather
 * than through a floating-UI layer that has no layout engine under happy-dom.
 *
 * ## Reuse
 *
 * The thread is `FeedbackTimeline` and the reply box is
 * `FeedbackCommentForm` — the same two components `/feedback/[id]` renders,
 * so an in-context reply is indistinguishable from one left on the detail
 * page. The thread itself comes from the existing `getFeedbackDetail` action,
 * which already enforces the admin-or-author visibility rule; fetching it per
 * open rather than shipping every comment down with every anchor keeps the
 * page-load cost of feedback mode proportional to the pins, not to the
 * conversation behind them.
 *
 * ## Injection safety
 *
 * Comment and summary text is rendered as React children — interpolated, so
 * escaped. Nothing here uses `dangerouslySetInnerHTML`, and it must stay that
 * way: this panel floats over an arbitrary application page, so a successful
 * injection here reads whatever that page is showing.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useTransition,
} from 'react'
import {
  AlertTriangle,
  Check,
  Loader2,
  MessageSquarePlus,
  X,
} from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { toast, toastError } from '@/lib/toast'
import { FEEDBACK_TRIAGE_NOTE_MAX_LENGTH } from '@/lib/feedback/limits'
import { cn } from '@/lib/utils'
import {
  getFeedbackDetail,
  type FeedbackDetail,
} from '@/app/actions/feedback-user'
import {
  triageFeedback,
  type FeedbackTriageAction,
} from '@/app/actions/feedback-triage'
import { FeedbackTimeline } from '@/components/feedback/feedback-timeline'
import { FeedbackCommentForm } from '@/components/feedback/feedback-comment-form'
import { getPinTint } from '@/components/feedback/feedback-status-utils'
import type { PositionedPin } from '@/components/feedback/pin-cluster'

export interface FeedbackPinPopoverProps {
  /** The threads behind this marker. Never empty. */
  pins: readonly PositionedPin[]
  /** Whether to offer the triage controls. */
  isAdmin: boolean
  /** Viewer's timezone, for the timeline's timestamps. */
  timezone: string
  /** Called after a triage lands, so the parent can refetch its anchors. */
  onTriaged?: () => void
}

/** The two dispositions that require the facilitator to type something. */
type NotedAction = Extract<FeedbackTriageAction, 'reject' | 'annotate'>

const NOTE_LABEL: Record<NotedAction, string> = {
  reject: 'Reason for rejecting (required)',
  annotate: 'Annotation (required)',
}

/**
 * Where the typed note ends up, said out loud under the field.
 *
 * Issue #13578 specifies Annotate as "an admin-only comment", and there is no
 * such thing to build here: `FeedbackComment` has no visibility column, and
 * `getFeedbackDetail` returns every comment on a thread to its submitter. The
 * note is additionally mirrored to the linked GitHub issue by
 * `triageFeedback`. So the control is admin-only in who may USE it, never in
 * who may READ the result.
 *
 * That gap is not closable on this branch — a visibility discriminator is a
 * schema change, and this issue's surface manifest declares `db` not
 * included. Until one exists, the honest move is to stop an admin-gated
 * button in an admin-only block reading as a private note: a facilitator
 * writing about a named employee has to be able to see, at the moment of
 * typing, that the employee will read it.
 */
const NOTE_VISIBILITY_HINT: Record<NotedAction, string> = {
  reject:
    'Visible to whoever submitted this feedback, and posted to the linked GitHub issue.',
  annotate:
    'Not private: visible to whoever submitted this feedback, and posted to the linked GitHub issue.',
}

export function FeedbackPinPopover({
  pins,
  isAdmin,
  timezone,
  onTriaged,
}: FeedbackPinPopoverProps) {
  const [activeId, setActiveId] = useState(pins[0].anchor.feedbackId)
  const [detail, setDetail] = useState<FeedbackDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [noteMode, setNoteMode] = useState<NotedAction | null>(null)
  const [note, setNote] = useState('')
  const [isPending, startTransition] = useTransition()
  const noteFieldId = useId()
  /**
   * Monotonic token for the in-flight `getFeedbackDetail`. Switching threads
   * inside a cluster starts a second request without cancelling the first, so
   * without this an older response can land last and put thread A's timeline
   * under thread B's header — the exact state the `setDetail(null)` comment
   * below says it is avoiding on the failure path.
   */
  const detailRequestRef = useRef(0)

  const active = pins.find(p => p.anchor.feedbackId === activeId) ?? pins[0]

  /**
   * Keep `activeId` inside the cluster.
   *
   * `active` already falls back to `pins[0]` for rendering, but `activeId` is
   * what `load`, `runTriage` and `FeedbackCommentForm` all address. When a
   * refetch drops the active thread (rejecting an item can stop it being an
   * anchor), leaving the state behind means the panel shows one thread while
   * a reply or a triage posts to another.
   */
  useEffect(() => {
    if (!pins.some(p => p.anchor.feedbackId === activeId)) {
      setActiveId(pins[0].anchor.feedbackId)
    }
  }, [pins, activeId])

  const load = useCallback(async (feedbackId: string) => {
    const token = ++detailRequestRef.current
    setIsLoading(true)
    const result = await getFeedbackDetail(feedbackId)
    if (detailRequestRef.current !== token) return
    // `setDetail(null)` on failure rather than leaving the PREVIOUS thread
    // mounted: showing thread A's comments under thread B's header is worse
    // than showing nothing.
    setDetail(result.success ? result.data : null)
    setIsLoading(false)
  }, [])

  // `activeId` is the whole dependency: switching threads inside a clustered
  // marker must refetch, and nothing else here should.
  useEffect(() => {
    void load(activeId)
  }, [activeId, load])

  const runTriage = (action: FeedbackTriageAction, text?: string) => {
    startTransition(async () => {
      const result = await triageFeedback({
        feedbackId: activeId,
        action,
        note: text,
      })
      if (result.success) {
        toast.success(result.message)
        setNoteMode(null)
        setNote('')
        void load(activeId)
        onTriaged?.()
      } else {
        toastError({
          message: result.error,
          context: { action: 'triageFeedback', feedbackId: activeId },
        })
      }
    })
  }

  const tint = getPinTint(active.anchor.status)
  const noteIsEmpty = note.trim().length === 0
  /**
   * A thread the #7521 intake safety screen is holding.
   *
   * Accept and Reject are hidden for it, because both would walk the row out
   * of `FLAGGED_FOR_REVIEW` without the disposition metadata and the
   * `feedback_safety.*` events that `releaseHeldFeedback` /
   * `dismissHeldFeedback` record. `triageFeedback` refuses it server-side as
   * well — this only stops offering a control that cannot work. Annotate
   * stays: a note about a held item is exactly what a facilitator wants to
   * leave before sending it back to the screen.
   */
  const isHeldForSafety = active.anchor.status === 'FLAGGED_FOR_REVIEW'

  return (
    /*
      #13788 — the height cap moved from the TIMELINE to the PANEL.
      
      It used to be `max-h-64` (256px) on the thread list alone, regardless of
      viewport, so on a full-height desktop window the conversation scrolled
      after two comments while the panel around it had hundreds of unused
      pixels. The cap belongs to the thing that must not outgrow the screen —
      the panel — and the timeline flexes inside it.

      `min(70vh, 40rem)`: 70vh so it always leaves the page it floats over
      visible, and a 640px ceiling so a very tall monitor does not produce a
      panel taller than anyone wants to read. `w-[28rem]` (448px) rather than
      `w-96` (384px): the header now carries the status chip and the summary
      on one row, which wrapped to three lines at 384px.
    */
    <div
      data-testid="feedback-pin-popover"
      className="flex w-[28rem] max-w-[90vw] max-h-[min(70vh,40rem)] flex-col"
    >
      {/*
        The HEADER. Over a `border-b` rather than floating above the timeline,
        so the panel reads as a document with a title rather than as a stack
        of unrelated rows — the same shape the user menu it now sits beside
        uses.
      */}
      <div
        data-testid="feedback-pin-header"
        className="flex flex-col gap-2 border-b border-stroke-extra-light pb-3"
      >
        {pins.length > 1 && (
          <div
            data-testid="feedback-pin-thread-switcher"
            className="flex flex-col gap-1"
          >
            {pins.map(p => (
              <button
                key={p.anchor.feedbackId}
                type="button"
                onClick={() => setActiveId(p.anchor.feedbackId)}
                aria-current={p.anchor.feedbackId === activeId}
                className={cn(
                  'text-left text-sm px-2 py-1 rounded truncate',
                  'hover:bg-surface-panel',
                  p.anchor.feedbackId === activeId && 'bg-surface-panel',
                )}
              >
                {p.anchor.summary ?? `Feedback ${p.anchor.feedbackId}`}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-start gap-2">
          <span
            className={cn(
              'px-2 py-0.5 rounded text-xs font-medium shrink-0',
              tint.chip,
            )}
          >
            {active.anchor.status.replace(/_/g, ' ').toLowerCase()}
          </span>
          {active.anchor.summary && (
            <p className="text-sm text-text-primary min-w-0">
              {active.anchor.summary}
            </p>
          )}
        </div>

        {!active.resolved && (
          /*
            Edge case 1. The overlay's existing `resolved: false` fallback
            draws the pin where the element used to be; this is the
            explanation that goes with it, and a "See in context" landing on a
            page that has since changed reaches exactly this state. Reused
            unchanged — nothing new was added for the deep link.
          */
          <p className="flex items-start gap-2 text-xs text-text-secondary">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              The element this feedback pointed at is no longer on the page, so
              the pin is showing where it used to be.
            </span>
          </p>
        )}
      </div>

      {/*
        The SCROLL region, and the only one. `flex-1 min-h-0` is what makes it
        give way first: without `min-h-0` a flex child refuses to shrink below
        its content, so the panel would grow past its own cap and push the
        triage footer off the bottom of the screen — the failure this layout
        exists to prevent.
      */}
      <div
        data-testid="feedback-pin-thread"
        className="min-h-0 flex-1 overflow-y-auto py-3"
      >
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : detail ? (
          <FeedbackTimeline
            timeline={detail.timeline}
            feedbackCreatedAt={detail.createdAt}
            timezone={timezone}
          />
        ) : (
          <p className="text-sm text-text-secondary">
            This feedback is no longer available.
          </p>
        )}
      </div>

      {/* Only when the thread actually loaded. Offering a working reply box
          under "This feedback is no longer available." invites a comment on a
          thread the viewer cannot see.

          OUTSIDE the scroll region since #13788: this is the control the
          whole issue is about being able to reach, and a reply box you have
          to scroll a conversation past is one more reason nobody replies. */}
      {detail !== null && (
        <div
          data-testid="feedback-pin-reply"
          className="border-t border-stroke-extra-light pt-3"
        >
          <FeedbackCommentForm
            feedbackId={activeId}
            compact
            onSuccess={() => void load(activeId)}
          />
        </div>
      )}

      {isAdmin && (
        <div
          data-testid="feedback-pin-triage"
          className="flex flex-wrap gap-2 border-t border-stroke-extra-light pt-3 mt-3"
        >
          {!isHeldForSafety && (
            <>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={isPending}
                onClick={() => runTriage('accept', undefined)}
              >
                {isPending ? (
                  <Loader2
                    className="w-4 h-4 animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <Check className="w-4 h-4" aria-hidden="true" />
                )}
                Accept
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isPending}
                onClick={() => {
                  setNote('')
                  setNoteMode('reject')
                }}
              >
                <X className="w-4 h-4" aria-hidden="true" />
                Reject
              </Button>
            </>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={isPending}
            onClick={() => {
              setNote('')
              setNoteMode('annotate')
            }}
          >
            <MessageSquarePlus className="w-4 h-4" aria-hidden="true" />
            Annotate
          </Button>
        </div>
      )}

      {isAdmin && isHeldForSafety && (
        <p
          data-testid="feedback-pin-safety-hold"
          className="flex items-start gap-2 text-xs text-text-secondary"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            The intake safety screen is holding this feedback. Release or
            dismiss it from the admin feedback queue — accepting or rejecting it
            here would skip that review.
          </span>
        </p>
      )}

      {isAdmin && noteMode && (
        <div className="flex flex-col gap-2">
          <label
            htmlFor={noteFieldId}
            className="text-xs font-medium text-text-secondary"
          >
            {NOTE_LABEL[noteMode]}
          </label>
          <p
            data-testid="feedback-pin-note-visibility"
            className="text-xs text-text-secondary"
          >
            {NOTE_VISIBILITY_HINT[noteMode]}
          </p>
          <Textarea
            id={noteFieldId}
            required
            rows={3}
            // Same cap the action enforces, so the field cannot accept text
            // the server will reject. `FEEDBACK_TRIAGE_NOTE_MAX_LENGTH` is
            // imported from `@/lib/feedback/limits` rather than re-typed —
            // that module exists because these caps used to drift between
            // client and server (#7520).
            maxLength={FEEDBACK_TRIAGE_NOTE_MAX_LENGTH}
            value={note}
            onChange={e => setNote(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              // The disabled state mirrors the server's guard rather than
              // replacing it: `triageFeedback` re-validates, because a
              // disabled button is a courtesy and not a control.
              disabled={noteIsEmpty || isPending}
              onClick={() => runTriage(noteMode, note.trim())}
            >
              {noteMode === 'reject' ? 'Confirm reject' : 'Confirm annotate'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setNoteMode(null)
                setNote('')
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
